import React, { useState, useEffect } from 'react';
import RCalendar, { CalendarProps as RCalendarProps } from 'react-calendar';
import { CalendarWrapper } from './Calendar.styles';
import { Icon } from '../../Icon/Icon';

export interface CalendarProps extends RCalendarProps {
  isRange?: boolean;
  // Opt-in for pickers whose minDate can sit in a later month, where the default view would open
  // with every day disabled.
  openAtMinDate?: boolean;
}

export const Calendar: React.FC<CalendarProps> = ({
  isRange = false,
  openAtMinDate = false,
  value,
  minDate,
  ...rest
}) => {
  // Popover pickers keep this component mounted while hidden, so the opening view has to track
  // minDate rather than being resolved once. Only a minDate in a later month is worth honouring;
  // anything earlier leaves today's month with selectable days.
  const futureMinDateTime =
    openAtMinDate && minDate && minDate.getTime() > Date.now() ? minDate.getTime() : undefined;

  const [activeStartDate, setActiveStartDate] = useState(() => {
    if (value) {
      return isRange ? value[0] : value;
    }

    if (futureMinDateTime !== undefined) {
      return new Date(futureMinDateTime);
    }

    return new Date();
  });

  useEffect(() => {
    if (value) {
      setActiveStartDate(isRange ? value[0] : value);
      return;
    }

    if (futureMinDateTime !== undefined) {
      setActiveStartDate(new Date(futureMinDateTime));
    }
  }, [isRange, value, futureMinDateTime]);

  const handleActiveStartDateChange = ({ activeStartDate: _activeStartDate }) => {
    setActiveStartDate(_activeStartDate);
  };

  return (
    <CalendarWrapper>
      <RCalendar
        calendarType="US"
        value={value}
        minDate={minDate}
        activeStartDate={activeStartDate}
        onActiveStartDateChange={handleActiveStartDateChange}
        selectRange={isRange}
        next2Label={null}
        prev2Label={null}
        prevLabel={<Icon className="fas fa-chevron-left" fontSize="h6" color="neutral.500" />}
        nextLabel={<Icon className="fas fa-chevron-right" fontSize="h6" color="neutral.500" />}
        {...rest}
      />
    </CalendarWrapper>
  );
};
